import {
  throwOutcomesMatch,
  type BeerPongBallPoint,
  type BeerPongPoint,
  type BeerPongThrowOutcome,
} from '@awwyeah/shared';
import {
  BEERPONG_FRAME_STRIDE,
  BEERPONG_PHYSICS_DT,
  simulateThrow,
  type BeerPongFrame,
} from './physics';

/**
 * The render half of the fixed-timestep split (see `physics.ts`) plus the snapshot-trust
 * guard — the same two jobs Pool's `playback.ts` does, kept out of the React component so
 * both are testable without a canvas:
 *
 *  1. `replayThrow` — re-simulate a throw from the move log and reconcile the result against
 *     the thrower's recorded snapshot.
 *  2. `playThrow` — walk the sample buffer in wall-clock time on `requestAnimationFrame`,
 *     interpolating between the two straddling 60 Hz samples, so the arc is smooth on any
 *     display at any refresh rate without the physics ever seeing a real elapsed time.
 */

/** Wall-clock milliseconds between two recorded samples if played at true speed. */
export const BEERPONG_FRAME_MS = BEERPONG_PHYSICS_DT * BEERPONG_FRAME_STRIDE * 1000;

/** No throw animation outruns this. A ball rolling to a halt for six seconds is *accurate*
 * and *boring*; past this the playback is time-compressed rather than truncated. */
export const BEERPONG_MAX_ANIMATION_MS = 3200;

export interface BeerPongThrowSimulation {
  /** What the app should treat as true — the recorded snapshot when there is one. */
  outcome: BeerPongThrowOutcome;
  frames: BeerPongFrame[];
  /** True when our own re-simulation disagreed with the recorded snapshot and we deferred to
   * the recording. Surfaced so the UI can note it rather than pretend nothing happened. */
  diverged: boolean;
}

/**
 * Re-simulates a throw from its move-log inputs, then reconciles with the thrower's snapshot.
 * **The snapshot always wins.** If our result differs we log a warning — loudly, with the
 * numbers — and use the recording anyway, because two phones showing a different number of
 * cups is broken in a way that "but our physics was right" doesn't fix. The warning is how we
 * find out whether cross-device drift is real (`physics.ts` argues it can't be) before we
 * decide to trust determinism alone.
 *
 * When there is no recorded snapshot — the thrower's own client, taking the throw for the
 * first time — our simulation *is* the truth and gets recorded.
 */
export function replayThrow(
  standing: readonly number[],
  shot: { aimX: number; aimY: number; power: number },
  recorded?: BeerPongThrowOutcome,
): BeerPongThrowSimulation {
  const sim = simulateThrow(standing, shot);
  if (!recorded) return { outcome: sim.outcome, frames: sim.frames, diverged: false };

  if (throwOutcomesMatch(sim.outcome, recorded)) {
    return { outcome: recorded, frames: sim.frames, diverged: false };
  }

  console.warn(
    '[beerpong] local simulation diverged from the recorded throw — trusting the recording.',
    { local: sim.outcome, recorded, shot },
  );
  // Play our frames (they're the only motion we have) but land on the recorded resting place,
  // so the animation and the state it ends in agree.
  const last = sim.frames[sim.frames.length - 1];
  const frames = [...sim.frames, { x: recorded.rest.x, y: recorded.rest.y, z: last?.z ?? 0 }];
  return { outcome: recorded, frames, diverged: true };
}

/** Linear interpolation between two samples. */
export function interpolateBall(
  a: BeerPongBallPoint,
  b: BeerPongBallPoint,
  t: number,
): BeerPongBallPoint {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  };
}

export interface BeerPongPlaybackHandle {
  /** Stops the animation immediately; safe to call twice. */
  cancel: () => void;
}

/**
 * Plays `frames` in wall-clock time, calling `onFrame` once per `requestAnimationFrame` with
 * an interpolated position, then `onDone` once. Long flights are time-compressed to
 * `BEERPONG_MAX_ANIMATION_MS` rather than cut off — you always see how the throw ended.
 */
export function playThrow(
  frames: readonly BeerPongFrame[],
  onFrame: (ball: BeerPongBallPoint) => void,
  onDone: () => void,
): BeerPongPlaybackHandle {
  const spans = Math.max(1, frames.length - 1);
  const trueDuration = spans * BEERPONG_FRAME_MS;
  const timeScale = Math.max(1, trueDuration / BEERPONG_MAX_ANIMATION_MS);
  let raf = 0;
  let cancelled = false;
  const started = performance.now();

  const tick = (now: number): void => {
    if (cancelled) return;
    const simElapsed = (now - started) * timeScale;
    const position = simElapsed / BEERPONG_FRAME_MS;
    if (position >= spans) {
      const last = frames[spans];
      if (last) onFrame({ ...last });
      onDone();
      return;
    }
    const index = Math.floor(position);
    const a = frames[index];
    const b = frames[index + 1];
    if (a && b) onFrame(interpolateBall(a, b, position - index));
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  return {
    cancel: () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    },
  };
}

/**
 * Drag → throw, the same catapult idiom Pool's `aimFromDrag` established: pull back *from*
 * the throwing spot and let go, and the ball flies along the vector from your finger toward
 * the spot. Pulling further doesn't only add power, it makes the aim *finer* — the same
 * finger movement subtends a smaller angle the further out you are — which is the whole
 * fine-adjust story on a phone.
 *
 * Power is drag distance over `fullPowerDistance`, clamped; under `deadZone` the throw is
 * cancelled, which is the "let go near the spot to change your mind" escape hatch — no
 * confirm button, just a place to release that means "never mind".
 *
 * The one difference from Pool: this returns the aim as a **vector**, never an angle, because
 * an angle would have to go back through `Math.sin`/`Math.cos` to become a velocity and those
 * are the only implementation-approximated functions anywhere near the trajectory (see
 * `physics.ts`).
 */
export interface BeerPongAim {
  aimX: number;
  aimY: number;
  power: number;
  /** Releasing here cancels instead of throwing. */
  cancel: boolean;
  /** Raw drag distance, in table units — for the power arc. */
  distance: number;
}

export function aimFromDrag(
  origin: BeerPongPoint,
  dragTo: BeerPongPoint,
  fullPowerDistance: number,
  deadZone: number,
): BeerPongAim {
  const dx = origin.x - dragTo.x;
  const dy = origin.y - dragTo.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  if (distance < 1e-6) return { aimX: 1, aimY: 0, power: 0, cancel: true, distance: 0 };
  return {
    aimX: dx,
    aimY: dy,
    power: Math.min(1, distance / fullPowerDistance),
    cancel: distance < deadZone,
    distance,
  };
}
